import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useRouter } from 'next/router'
import { SubscriptionPlans } from '@pff-consumer/schema'
import { Toast } from '@pff-consumer/tweed-ui/atoms/toast/toast'
import { LoadingPane } from '@pff-consumer/tweed-ui/molecules/loading-pane/loading-pane'
import { IDME_VERIFICATION_ERROR_MESSAGE } from '@pff-consumer/utils/lib/accounts/constants'
import {
  useGetSubscriptionOverviewQuery,
  useUpdateUserIdmeIdentityMutation,
} from '@pff-consumer/data-provider/lib/account/account-api'
import { useFeatureFlags } from '@pff-consumer/feature-flags'
import dynamic from 'next/dynamic'
import { checkoutHooks } from '@pff-consumer/data-provider/lib/checkout/checkout.hooks'
import { useSubscribeAnalyticsEvent } from '@pff-consumer/feature-account/hooks/use-subscribe-analytics-event.web'
import { getIdmeGroupInfo } from '@pff-consumer/utils/lib/accounts/get-id-me-group-info'
import { LeftPane } from '../left-pane/left-pane'
import { Header } from '../common/header/header'
import { useStyles } from './layout.style'
import { RecurlyContainerType } from '../recurly-container/recurly-container'
import { Banner } from '../common/banner/banner'
import { LayoutProps } from './layout.props'

const RecurlyPaymentSection = dynamic<React.ComponentProps<RecurlyContainerType>>(
  () => import('../recurly-container/recurly-container').then((res) => res.RecurlyContainer),
  {
    ssr: false,
  }
)

const { useCheckoutActions, useGetIsLoggedIn, usePromoCode } = checkoutHooks

export const Layout = (props: LayoutProps) => {
  const { email, grootUid, isUSUser } = props
  const { setDefaultEmail, setGrootUid, updateMonthlyPlan, updateVariant, setNewsLetterPreference, setIsLoggedIn } =
    useCheckoutActions()
  const styles = useStyles()
  const router = useRouter()
  const { getFeatureFlag } = useFeatureFlags()
  const isLoggedIn = useGetIsLoggedIn()
  const promoCode = usePromoCode()
  const showBanner = getFeatureFlag<boolean>('subscribe-upsell-banner', true)
  const { isLoading } = useGetSubscriptionOverviewQuery(undefined, { skip: !isLoggedIn })
  const [updateUserIdentity, { isLoading: isMutationLoading, isError }] = useUpdateUserIdmeIdentityMutation()
  const [openToast, setOpenToast] = useState<boolean>(false)

  // mutation call was happening twice, so to prevent that, this has been added
  const hasCalledMutation = useRef<boolean>(false)
  const { sendSubscribeAnalyticsEvent } = useSubscribeAnalyticsEvent()
  const { groupLabel, isIdme } = getIdmeGroupInfo(promoCode)

  const handleToastClose = useCallback(() => {
    setOpenToast(false)
  }, [])

  // TODO update feature flags to be SSR in so we don't have to update the fallback
  const monthlyPlan = getFeatureFlag<string>(
    'subscribe-monthly-variant',
    SubscriptionPlans.PREMIUM_MONTHLY_SEASON
  ) as SubscriptionPlans

  useEffect(() => {
    const { code, ...rest } = router.query

    if (typeof code === 'string' && !hasCalledMutation.current) {
      updateUserIdentity({ authCode: code })
      hasCalledMutation.current = true

      router.replace(
        {
          pathname: router.pathname,
          query: { ...rest },
        },
        undefined,
        { shallow: true }
      )
    }
  }, [])

  useEffect(() => {
    if (isError) {
      setOpenToast(true)
    }
  }, [isError])

  useEffect(() => {
    if (isIdme && groupLabel) {
      sendSubscribeAnalyticsEvent('ID.me Verified', {
        discount: groupLabel,
        experience: 'Single Page Checkout',
      })
    }
  }, [isIdme, groupLabel, sendSubscribeAnalyticsEvent])

  useEffect(() => {
    updateMonthlyPlan(monthlyPlan)
    updateVariant(monthlyPlan)
  }, [monthlyPlan])

  useEffect(() => {
    setDefaultEmail(email)
    if (grootUid) {
      setGrootUid(grootUid)
      setIsLoggedIn(true)
    } else {
      // set email opt in preference for non-logged in users based on the nationality
      setNewsLetterPreference(isUSUser)
    }
    // we only want to run this once on mount
  }, [])

  /**
   * The layout needs to turn SSR off for this component because recurly does not support SSR. This means we need to memoize the payment form to prevent this re-rendering and re-fetching recurly scripts over again
   */
  const PaymentForm = useMemo(() => <RecurlyPaymentSection />, [])

  if (isLoading || isMutationLoading) return <LoadingPane isLoading />

  return (
    <div className={styles.container}>
      <div className={styles.leftPane}>
        <LeftPane />
      </div>
      <div
        id='desktopFormScrollContainer' // added to set scrolling to top and bottom
        className={styles.rightPane}
      >
        <div className={styles.mobileOnly}>
          <Header />
        </div>
        {showBanner && <Banner />}
        {PaymentForm}
      </div>
      <Toast
        showToast={openToast}
        hasErrored
        errorMessage={IDME_VERIFICATION_ERROR_MESSAGE}
        onClose={handleToastClose}
      />
    </div>
  )
}
